/**
 * AppLauncher Component
 *
 * Microsoft 365-style waffle menu for navigating between apps.
 * Features a 9-dot grid button that opens a dropdown with app tiles.
 *
 * Features:
 * - 9-dot grid button (waffle icon)
 * - Dropdown with categorized app tiles organized by dashboard sections
 * - Uses adminEditorStore for app grid items
 * - Filters to only visible items, sorted by order
 * - Click outside closes the launcher
 * - Keyboard accessible (Escape to close)
 * - Dashboard card sections with visual headers and icons
 *
 * @example
 * ```tsx
 * // In TopBar
 * <AppLauncher />
 * ```
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Building2,
  FileText,
  Phone,
  Mail,
  Radio,
  Terminal,
  Database,
  RefreshCw,
  Receipt,
  Kanban,
  Clock,
  Brain,
  BookOpen,
  CalendarDays,
  Upload,
  Inbox,
  SendHorizonal,
  FolderOpen,
  LayoutGrid,
  Search,
  Target,
  MessageCircle,
  Bot,
  Code2,
  LucideIcon,
} from 'lucide-react';
import { PanelRight } from 'lucide-react';
import { ROUTES } from '@/app/routes';
import { useVisibleAppGridItems } from '@features/admin/store/adminEditorStore';
import { useAppPanelStore } from '@shared/store/useAppPanelStore';
import { AppLauncherTile } from './AppLauncherTile';

// ============================================================================
// Dashboard Card Configuration
// ============================================================================

/**
 * Dashboard card item for the launcher menu.
 * Matches the structure from DashboardView.tsx card configurations.
 */
interface DashboardCardItem {
  id: string;
  icon: LucideIcon;
  iconColor: string;
  title: string;
  description: string;
  route: string;
}

/**
 * Dashboard section with cards.
 */
interface DashboardSection {
  id: string;
  title: string;
  cards: DashboardCardItem[];
}

/**
 * Get all dashboard sections with their cards.
 * Organized to match the 5 sections from DashboardView.
 *
 * NOTE: This is a function (not a constant) to avoid circular dependency
 * with ROUTES. ROUTES is defined in routes.tsx which imports AppShell,
 * which imports AppLauncher. If we access ROUTES at module init time,
 * it causes "Cannot access 'ROUTES' before initialization" error.
 */
const getDashboardSections = (): DashboardSection[] => [
  {
    id: 'campaign-quick-access',
    title: 'Campaign Quick-Access',
    cards: [
      {
        id: 'event-planning',
        icon: Calendar,
        iconColor: 'purple',
        title: 'Event Planning',
        description: 'Plan and schedule events on the 12-month annual calendar grid',
        route: ROUTES.EPG,
      },
      {
        id: 'organizations',
        icon: Building2,
        iconColor: 'green',
        title: 'Organizations',
        description: 'Browse and manage organizations from your Pipedrive CRM',
        route: ROUTES.ORGANIZATIONS,
      },
    ],
  },
  {
    id: 'post-campaign-execution',
    title: 'Post-Campaign',
    cards: [
      {
        id: 'weekly-drop-report',
        icon: FileText,
        iconColor: 'green',
        title: 'Weekly Drop Report',
        description: 'View dropped organizations grouped by week',
        route: ROUTES.WEEKLY_DROP_REPORT,
      },
      {
        id: 'weekly-call-status',
        icon: Phone,
        iconColor: 'blue',
        title: 'Weekly Call Status',
        description: 'Track call activities by time period',
        route: ROUTES.WEEKLY_CALL_STATUS,
      },
      {
        id: 'follow-ups',
        icon: Mail,
        iconColor: 'orange',
        title: 'Follow-Ups',
        description: 'Manage email follow-ups for your drops',
        route: ROUTES.DROP_FOLLOW_UPS,
      },
    ],
  },
  {
    id: 'administrative',
    title: 'Administrative',
    cards: [
      {
        id: 'ai-assistant',
        icon: MessageCircle,
        iconColor: 'brand',
        title: 'AI Assistant',
        description: 'LibreChat workspace for chat, docs, tools, and artifacts',
        route: ROUTES.ASSISTANT,
      },
      {
        id: 'vibe-remote',
        icon: Radio,
        iconColor: 'brand',
        title: 'Vibe Remote',
        description: 'Remote terminal sessions',
        route: ROUTES.VIBE_REMOTE,
      },
      {
        id: 'ops-monitor',
        icon: Bot,
        iconColor: 'brand',
        title: 'Ops Monitor',
        description: 'Live commit feed & session activity',
        route: ROUTES.CLAWD_BOT,
      },
      {
        id: 'inbox',
        icon: Inbox,
        iconColor: 'blue',
        title: 'Inbox',
        description: 'Outlook inbox monitoring and message triage',
        route: ROUTES.INBOX,
      },
      {
        id: 'cli-dashboard',
        icon: Terminal,
        iconColor: 'gray',
        title: 'CLI Dashboard',
        description: 'Monitor CLI sessions in real-time',
        route: ROUTES.CLI_DASHBOARD,
      },
      {
        id: 'field-management',
        icon: Database,
        iconColor: 'cyan',
        title: 'Field Management',
        description: 'Manage Pipedrive field definitions',
        route: ROUTES.FIELD_MANAGEMENT,
      },
      {
        id: 'pug-records',
        icon: RefreshCw,
        iconColor: 'green',
        title: 'PUG Records',
        description: 'View Pipedrive sync records',
        route: ROUTES.PUG_RECORDS,
      },
      {
        id: 'expenses',
        icon: Receipt,
        iconColor: 'orange',
        title: 'Expenses',
        description: 'Upload receipts with OCR',
        route: ROUTES.EXPENSES,
      },
      {
        id: 'project-board',
        icon: Kanban,
        iconColor: 'purple',
        title: 'Project Board',
        description: 'Kanban-style project management',
        route: ROUTES.PLANKA,
      },
      {
        id: 'file-manager',
        icon: FolderOpen,
        iconColor: 'blue',
        title: 'File Manager',
        description: 'Manage files and screenshots',
        route: ROUTES.FILE_MANAGER,
      },
      {
        id: 'boards',
        icon: LayoutGrid,
        iconColor: 'cyan',
        title: 'Boards',
        description: 'Weekly planning and task boards',
        route: ROUTES.BOARDS,
      },
    ],
  },
  {
    id: 'client-management',
    title: 'Client Management',
    cards: [
      {
        id: 'client-timelines',
        icon: Calendar,
        iconColor: 'cyan',
        title: 'Client Timelines',
        description: 'Manage enrollment timelines',
        route: ROUTES.TIMELINES,
      },
      {
        id: 'availability',
        icon: Clock,
        iconColor: 'purple',
        title: 'Availability',
        description: 'Find meeting times across teams',
        route: ROUTES.AVAILABILITY,
      },
    ],
  },
  {
    id: 'sales-tools',
    title: 'Sales Tools',
    cards: [
      {
        id: 'flashcards',
        icon: Brain,
        iconColor: 'purple',
        title: 'Flashcards',
        description: 'Study outreach scripts and tips',
        route: ROUTES.FLASHCARDS,
      },
      {
        id: 'notes',
        icon: BookOpen,
        iconColor: 'blue',
        title: 'Notes',
        description: 'Organize notes and documents',
        route: ROUTES.NOTES,
      },
      {
        id: 'houston-calendar',
        icon: CalendarDays,
        iconColor: 'orange',
        title: 'Houston Calendar',
        description: 'Marketing calendar for Houston events',
        route: ROUTES.CALENDAR,
      },
      {
        id: 'hmc-events',
        icon: Upload,
        iconColor: 'cyan',
        title: 'HMC Events',
        description: 'Upload HMC files and view reports',
        route: ROUTES.HMC_EVENTS,
      },
      {
        id: 'file-inbox',
        icon: Inbox,
        iconColor: 'green',
        title: 'File Inbox',
        description: 'Email attachments from Gmail',
        route: ROUTES.FILE_INBOX,
      },
      {
        id: 'usi-events',
        icon: SendHorizonal,
        iconColor: 'purple',
        title: 'USI Events',
        description: 'Send postcard emails to contacts',
        route: ROUTES.USI_EVENTS,
      },
      {
        id: 'opportunities-analyzer',
        icon: Search,
        iconColor: 'orange',
        title: 'Opportunities Analyzer',
        description: 'Compare OMNI vs Pipedrive opportunities',
        route: ROUTES.OPPORTUNITIES_ANALYZER,
      },
      {
        id: 'business-plan',
        icon: Target,
        iconColor: 'green',
        title: 'Business Plan',
        description: 'Manage campaign business plans and targets',
        route: ROUTES.BUSINESS_PLAN,
      },
    ],
  },
];

/**
 * Map icon color names to Tailwind CSS classes
 */
const ICON_COLOR_CLASSES: Record<string, string> = {
  purple: 'text-purple-400',
  green: 'text-green-400',
  brand: 'text-brand-500',
  blue: 'text-blue-400',
  orange: 'text-orange-400',
  gray: 'text-dark-muted',
  cyan: 'text-cyan-400',
};

// ============================================================================
// Waffle Icon Component
// ============================================================================

/**
 * 3x3 dot grid icon (waffle/app launcher icon).
 * Custom SVG component matching Microsoft 365 style.
 */
const WaffleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Row 1 */}
    <circle cx="5" cy="5" r="2" />
    <circle cx="12" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    {/* Row 2 */}
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
    {/* Row 3 */}
    <circle cx="5" cy="19" r="2" />
    <circle cx="12" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
  </svg>
);

// ============================================================================
// Component Props
// ============================================================================

export interface AppLauncherProps {
  /** Additional CSS classes for the container */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * App Launcher component with waffle menu pattern.
 * Displays visible app grid items in a dropdown grid.
 * Includes all dashboard card sections organized by category.
 */
export const AppLauncher: React.FC<AppLauncherProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPanelFocused, setIsPanelFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // Get visible app grid items sorted by order (legacy app grid)
  const appGridItems = useVisibleAppGridItems();

  // App panel store for "Open as Panel" functionality
  const { openPanel } = useAppPanelStore();

  // Dashboard sections for the launcher (memoized, calls function to avoid circular dep)
  const dashboardSections = useMemo(() => getDashboardSections(), []);

  // Flatten all cards for keyboard navigation
  const allCards = useMemo(() => {
    return dashboardSections.flatMap(section => section.cards);
  }, [dashboardSections]);

  // Reset selection when menu opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
      setIsPanelFocused(false);
    }
  }, [isOpen]);

  // Listen for global 'open-app-launcher' event (triggered by 'A' keyboard shortcut)
  useEffect(() => {
    const handleOpenAppLauncher = () => {
      setIsOpen(true);
      // Focus the button first so keyboard events work
      setTimeout(() => buttonRef.current?.focus(), 50);
    };

    window.addEventListener('open-app-launcher', handleOpenAppLauncher);
    return () => window.removeEventListener('open-app-launcher', handleOpenAppLauncher);
  }, []);

  // ========================================================================
  // Event Handlers
  // ========================================================================

  /**
   * Handle tile click - navigate and close launcher
   */
  const handleTileClick = useCallback(
    (route: string) => {
      navigate(route);
      setIsOpen(false);
    },
    [navigate]
  );

  /**
   * Handle "Open as Panel" click - opens route in side panel
   */
  const handleOpenAsPanel = useCallback(
    (route: string, title: string) => {
      openPanel(route, title);
      setIsOpen(false);
    },
    [openPanel]
  );

  /**
   * Toggle launcher open/closed
   */
  const toggleLauncher = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  /**
   * Close launcher
   */
  const closeLauncher = useCallback(() => {
    setIsOpen(false);
  }, []);

  // ========================================================================
  // Click Outside Handler
  // ========================================================================

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          setIsOpen(false);
          break;
        case 'ArrowDown':
          event.preventDefault();
          setIsPanelFocused(false);
          setSelectedIndex((prev) => (prev + 1) % allCards.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setIsPanelFocused(false);
          setSelectedIndex((prev) => (prev - 1 + allCards.length) % allCards.length);
          break;
        case 'Tab':
          // Tab toggles between Navigate and Open as Panel
          event.preventDefault();
          setIsPanelFocused((prev) => !prev);
          break;
        case 'ArrowRight':
          if (!isPanelFocused) {
            event.preventDefault();
            setIsPanelFocused(true);
          }
          break;
        case 'ArrowLeft':
          if (isPanelFocused) {
            event.preventDefault();
            setIsPanelFocused(false);
          }
          break;
        case 'Enter':
          event.preventDefault();
          if (allCards[selectedIndex]) {
            const card = allCards[selectedIndex];
            if (isPanelFocused) {
              handleOpenAsPanel(card.route, card.title);
            } else {
              handleTileClick(card.route);
            }
          }
          break;
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, allCards, selectedIndex, isPanelFocused, handleTileClick, handleOpenAsPanel]);

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Waffle Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleLauncher}
        className="p-2 rounded-lg hover:bg-dark-surface transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500/50"
        aria-label="Open app launcher (A)"
        aria-expanded={isOpen}
        aria-haspopup="true"
        title="App Launcher (Press A)"
      >
        <WaffleIcon className="w-5 h-5 text-dark-muted hover:text-dark-text-primary transition-all duration-150" />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-dark-surface border border-dark-border rounded-xl shadow-2xl shadow-black/20 min-w-[320px] max-w-[400px] max-h-[70vh] overflow-y-auto"
          role="menu"
          aria-orientation="vertical"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-dark-border/30">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-dark-text-primary">Apps</h2>
              <span className="text-[10px] text-dark-muted">
                ↑↓ navigate • Tab panel • Enter select
              </span>
            </div>
          </div>

          {/* Dashboard Sections with Cards */}
          <div className="py-2">
            {(() => {
              let flatIndex = 0;
              return dashboardSections.map((section, sectionIndex) => (
                <div key={section.id}>
                  {/* Section Header */}
                  <div className="px-4 py-2">
                    <span className="text-xs font-medium text-dark-muted uppercase tracking-wider">
                      {section.title}
                    </span>
                  </div>

                  {/* Section Cards */}
                  <div className="px-2 pb-2">
                    {section.cards.map((card) => {
                      const Icon = card.icon;
                      const colorClass = ICON_COLOR_CLASSES[card.iconColor] || 'text-brand-500';
                      const cardIndex = flatIndex++;
                      const isSelected = cardIndex === selectedIndex;
                      const showPanelHighlight = isSelected && isPanelFocused;

                      return (
                        <div
                          key={card.id}
                          onMouseEnter={() => {
                            setSelectedIndex(cardIndex);
                            setIsPanelFocused(false);
                          }}
                          className={`flex items-center rounded-lg transition-all duration-150 group ${
                            isSelected ? 'bg-dark-surface/50' : 'hover:bg-dark-surface/50'
                          }`}
                        >
                          {/* Main tile — navigates */}
                          <button
                            type="button"
                            onClick={() => handleTileClick(card.route)}
                            className={`flex-1 flex items-center gap-3 px-3 py-2 text-left min-w-0 rounded-l-lg ${
                              isSelected && !isPanelFocused ? 'ring-1 ring-inset ring-brand-500/50' : ''
                            }`}
                            role="menuitem"
                            title={card.description}
                          >
                            <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-dark-bg ${colorClass}`}>
                              <Icon size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-dark-text-primary truncate">
                                {card.title}
                              </div>
                              <div className="text-xs text-dark-muted truncate">
                                {card.description}
                              </div>
                            </div>
                          </button>

                          {/* Open as Panel button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAsPanel(card.route, card.title);
                            }}
                            className={`p-1.5 mr-2 rounded-md transition-all duration-150 ${
                              showPanelHighlight
                                ? 'opacity-100 text-brand-400 bg-brand-500/20 ring-1 ring-brand-500'
                                : isSelected
                                  ? 'opacity-100 text-dark-muted hover:text-brand-400 hover:bg-brand-500/10'
                                  : 'text-dark-muted opacity-0 group-hover:opacity-100 hover:text-brand-400 hover:bg-brand-500/10'
                            }`}
                            title={`Open ${card.title} as panel (Tab to select)`}
                            aria-label={`Open ${card.title} as side panel`}
                          >
                            <PanelRight size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Section Divider (except for last section) */}
                  {sectionIndex < dashboardSections.length - 1 && (
                    <div className="mx-4 border-b border-dark-border/30" />
                  )}
                </div>
              ));
            })()}
          </div>

          {/* Legacy App Grid (if needed) */}
          {appGridItems.length > 0 && (
            <>
              <div className="mx-4 border-b border-dark-border/30" />
              <div className="px-4 py-2">
                <span className="text-xs font-medium text-dark-muted uppercase tracking-wider">
                  More Apps
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 pt-0">
                {appGridItems.map((item) => (
                  <AppLauncherTile
                    key={item.id}
                    item={item}
                    onClick={() => handleTileClick(item.route)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Dev Docs - Tech Stack & Roadmap */}
          <div className="mx-4 border-t border-dark-border/30" />
          <div className="px-2 py-2">
            <button
              type="button"
              onClick={() => handleTileClick(ROUTES.APP_HOME)}
              className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg hover:bg-dark-surface/50 transition-all duration-150"
              role="menuitem"
              title="View tech stack, features, and development roadmap"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-dark-bg text-dark-muted">
                <Code2 size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-dark-text-primary">Dev Docs</div>
                <div className="text-xs text-dark-muted">Tech stack & roadmap</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLauncher;
